/**
 * Class rules for the blurred "your real panel" layer behind an upsell card.
 *
 * Every string here must appear LITERALLY in source — Tailwind's content scanner is a
 * regex over the file text, so building these by concatenation or interpolation silently
 * produces no CSS and every control stays visible.
 */

/**
 * The blur itself.
 *
 * Deliberately carries no `motion-reduce:` opt-out. A previous version had
 * `motion-reduce:blur-none motion-reduce:opacity-40`, which handed anyone with OS "Reduce
 * motion" enabled — a common setting, and a one-click toggle in DevTools — a fully legible
 * view of the gated panel. A static blur is not motion. Translucency is handled on the
 * scrim instead, where reducing it makes the card MORE readable and the panel LESS.
 */
export const PREVIEW_BLUR = 'blur-[6px]';

/**
 * Hides anything that reads as an affordance, so a blurred panel cannot look like it is
 * offering actions the plan does not include.
 *
 * `invisible` (visibility:hidden), never `hidden` (display:none): the point of a preview is
 * the SHAPE of the real screen, and collapsing boxes reflows it into something that no
 * longer reads as the product. Per spec a `visibility: hidden` element is also skipped by
 * sequential focus navigation, so this doubles as the keyboard fix.
 *
 * The `:not([aria-hidden='true'])` guard is load-bearing in this repo: `FunnelAreaChart`
 * builds the funnel geometry itself out of real `<button>` elements that mark themselves
 * decorative (`aria-hidden` + `tabIndex={-1}`). A blanket `[&_button]:invisible` would
 * erase the silhouette — the one thing the preview exists to show.
 */
export const PREVIEW_HIDE_CONTROLS = [
  "[&_button:not([aria-hidden='true'])]:invisible",
  '[&_a[href]]:invisible',
  '[&_input]:invisible',
  '[&_select]:invisible',
  '[&_textarea]:invisible',
  "[&_[role='button']]:invisible",
  "[&_[role='checkbox']]:invisible",
  "[&_[role='switch']]:invisible",
  "[&_[role='combobox']]:invisible",
  "[&_[role='menuitem']]:invisible",
  "[&_[role='tab']]:invisible",
  '[&_[contenteditable]]:invisible',
].join(' ');

/**
 * Optimize and Validation run to several thousand pixels. The upsell card is centred with
 * `absolute inset-0`, so without a clamp it lands far below the fold. The mask fades the
 * cut edge instead of leaving a hard slice through the middle of a chart.
 */
export const PREVIEW_CLAMP = [
  'max-h-[560px]',
  'overflow-hidden',
  '[mask-image:linear-gradient(to_bottom,black_70%,transparent_100%)]',
].join(' ');
